<template>
  <article>
    <AreaTopicsDoeBanner />

    <TheNavigation :trigger-id="triggerId" :navigation="navigation" />

    <TheScrollTrigger :offset="offset" @trigger="handleTrigger('challenges')">
      <AreaTopicsDoeChallenges id="challenges" />
    </TheScrollTrigger>

    <TheScrollTrigger
      :offset="offset"
      @trigger="handleTrigger('why-advantech')"
    >
      <AreaTopicsDoeWhyAdvantech id="why-advantech" />
    </TheScrollTrigger>

    <TheScrollTrigger
      :offset="offset"
      @trigger="handleTrigger('digital-journey')"
    >
      <AreaTopicsDoeDigitalJourney id="digital-journey" />
    </TheScrollTrigger>

    <TheScrollTrigger :offset="offset" @trigger="handleTrigger('use-cases')">
      <AreaTopicsDoeUseCases id="use-cases" />
    </TheScrollTrigger>

    <TheScrollTrigger :offset="offset" @trigger="handleTrigger('next-steps')">
      <AreaTopicsDoeGetStarted id="next-steps" />
    </TheScrollTrigger>
  </article>
</template>

<script setup lang="ts">
  const { t } = useI18n({ useScope: 'local' });

  const { headerHeight, navigationHeight, provideWebTitle } = useWebsite();
  const { arrived } = useScrollArrivedBottom();

  const triggerId = ref<string>('');

  const offset = computed(
    (): number => headerHeight.value + navigationHeight.value
  );

  const navigation = computed((): { id: string; name: string }[] => [
    { id: 'challenges', name: t('challenges') },
    { id: 'why-advantech', name: t('why-advantech') },
    { id: 'digital-journey', name: t('digital-journey') },
    { id: 'use-cases', name: t('use-cases') },
    { id: 'next-steps', name: t('next-steps') },
  ]);

  const handleTrigger = (id: string): void => {
    if (!arrived.value) {
      triggerId.value = id;
    }
  };

  watch(arrived, (value) => {
    if (value) {
      const id = navigation.value[navigation.value.length - 1]?.id || '';
      triggerId.value = id;
    }
  });

  useSeoMeta({
    title: () => t('seo-title'),
    ogTitle: () => provideWebTitle(t('seo-title')),
    twitterTitle: () => provideWebTitle(t('seo-title')),
    description: () => t('seo-description'),
    ogDescription: () => t('seo-description'),
    twitterDescription: () => t('seo-description'),
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "seo-title": "AI-Powered Digital O&M",
    "seo-description": "Advantech WISE Connects Site Data for Remote Operations, Multi-Site Visibility, and AI-Driven Optimization—Helping Energy and Utilities Operate More Reliably and Efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "next-steps": "Next Steps"
  },
  "en-jp": {
    "seo-title": "AI-Powered Digital O&M",
    "seo-description": "Advantech WISE Connects Site Data for Remote Operations, Multi-Site Visibility, and AI-Driven Optimization—Helping Energy and Utilities Operate More Reliably and Efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "next-steps": "Next Steps"
  },
  "en-tw": {
    "seo-title": "AI-Powered Digital O&M",
    "seo-description": "Advantech WISE Connects Site Data for Remote Operations, Multi-Site Visibility, and AI-Driven Optimization—Helping Energy and Utilities Operate More Reliably and Efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "next-steps": "Next Steps"
  },
  "en-kr": {
    "seo-title": "AI-Powered Digital O&M",
    "seo-description": "Advantech WISE Connects Site Data for Remote Operations, Multi-Site Visibility, and AI-Driven Optimization—Helping Energy and Utilities Operate More Reliably and Efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "next-steps": "Next Steps"
  },
  "en-americas": {
    "seo-title": "AI-Powered Digital O&M",
    "seo-description": "Advantech WISE Connects Site Data for Remote Operations, Multi-Site Visibility, and AI-Driven Optimization—Helping Energy and Utilities Operate More Reliably and Efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "next-steps": "Next Steps"
  },
  "en-asia-pacific": {
    "seo-title": "AI-Powered Digital O&M",
    "seo-description": "Advantech WISE Connects Site Data for Remote Operations, Multi-Site Visibility, and AI-Driven Optimization—Helping Energy and Utilities Operate More Reliably and Efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "next-steps": "Next Steps"
  },
  "en-emea": {
    "seo-title": "AI-Powered Digital O&M",
    "seo-description": "Advantech WISE Connects Site Data for Remote Operations, Multi-Site Visibility, and AI-Driven Optimization—Helping Energy and Utilities Operate More Reliably and Efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "next-steps": "Next Steps"
  },
  "ja-jp": {
    "seo-title": "デジタル維持管理（O&M）",
    "seo-description": "Advantech WISEは、現場・設備データを統合し、遠隔運用や複数拠点の可視化、AIによる最適化を実現。エネルギー・ユーティリティ企業の安定運用と効率向上を支援します。",
    "challenges": "O&Mの課題",
    "why-advantech": "Advantech WISEを選ぶ理由",
    "digital-journey": "デジタルO&Mジャーニー",
    "use-cases": "導入事例",
    "next-steps": "次のステップ"
  },
  "zh-tw": {
    "seo-title": "數位維運（O&M）",
    "seo-description": "Advantech WISE 整合現場數據，實現遠端監控、跨據點統一可視化及 AI 智慧優化，協助能源與公用事業提升營運效率與穩定性",
    "challenges": "維運挑戰",
    "why-advantech": "為何選擇研華WISE",
    "digital-journey": "數位維運旅程",
    "use-cases": "應用案例",
    "next-steps": "下一步"
  }
}
</i18n>
